import type { DirectoryPublic } from '@repo/types/directory';

import { DraggableDroppableDirectory } from '@/components/features/files/dnd';
import FsDirectoryCard from '@/components/features/files/FsDirectoryCard';
import FSEntityWrapper from '@/components/features/files/FSEntityWrapper';
import { useFSEntityInteraction } from '@/hooks/fs';

/**
 * Props for the Directory component.
 * @property {DirectoryPublic} directory - The directory data to display.
 * @property {(directoryId: string) => void} handleNavigateTo - Callback for navigation.
 */
interface DirectoryProps {
  directory: DirectoryPublic;
  handleNavigateTo: (directoryId: string) => void;
}

export default function Directory(directoryProps: DirectoryProps) {
  const { directory, handleNavigateTo } = directoryProps;
  const navigateToDirectory = () => {
    handleNavigateTo(directory.id);
  };

  const { isEntitySelected, isFocused, size, handleClick, handleKeyDown, handleDoubleClick, longPressHandlers } = useFSEntityInteraction({
    entity: directory,
    entityType: 'directory',
    onOpen: navigateToDirectory,
  });

  return (
    <DraggableDroppableDirectory directory={directory}>
      <FSEntityWrapper entity={directory} entityType="directory">
        <FsDirectoryCard
          aria-selected={isEntitySelected}
          data-selected={isEntitySelected ? 'true' : 'false'}
          data-focused={isFocused ? 'true' : 'false'}
          tabIndex={isFocused ? 0 : -1}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          onPointerDown={longPressHandlers.onPointerDown}
          onPointerMove={longPressHandlers.onPointerMove}
          onPointerUp={longPressHandlers.onPointerUp}
          directory={directory}
          disabledMenu={size > 1}
          className="aria-selected:bg-brand-accent/25 aria-selected:border-border-brand-accent aria-selected:ring-brand-accent data-[focused=true]:outline-focus-ring aria-selected:ring-3 data-[focused=true]:outline-3 data-[focused=true]:outline-offset-2"
        />
      </FSEntityWrapper>
    </DraggableDroppableDirectory>
  );
}
